import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { createCustomer, getCustomers } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { AddressFields, Field, customerName, validEmail } from './masterShared'
import { useSession } from '../../session'
import { CustomerMeasurements } from '../sales/Measurements'
import { returnTo } from '../../components/CreateOption'

const BLANK = { first_name: '', last_name: '', email: '', phone: '', address: '', city: '', state: '', postal_code: '', country: '' }

// One customer's contact details and address, used on their invoices.
export default function CustomerForm() {
  const { customerId } = useParams()
  const isNew = !customerId
  const nav = useNavigate()
  // Opened from "+ Add a new …" in a dropdown: go back there afterwards.
  const done = returnTo(useLocation().search, '/master/customers')
  const qc = useQueryClient()
  const { notify } = useToast()
  const { has, measurements } = useSession()
  const [form, setForm] = useState(BLANK)
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!isNew)

  useEffect(() => {
    if (isNew) { setForm(BLANK); return }
    setLoading(true)
    getCustomers()
      .then((rows) => {
        const c = (rows || []).find((r) => String(r.customer_id) === String(customerId))
        if (!c) { setError('This customer wasn’t found.'); return }
        setForm(Object.fromEntries(Object.keys(BLANK).map((k) => [k, c[k] || ''])))
      })
      .catch((e) => setError(e.message || 'Couldn’t load the customer.'))
      .finally(() => setLoading(false))
  }, [customerId, isNew])

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    const e = {}
    if (!form.first_name.trim() && !form.last_name.trim()) e.first_name = 'Give a first or last name (or the business name).'
    if (!validEmail(form.email)) e.email = 'That email doesn’t look right.'
    setErrors(e)
    if (Object.keys(e).length) { setError('Check the highlighted fields.'); return }
    setSaving(true)
    setError('')
    try {
      await createCustomer({ ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, String(v).trim()])), customer_id: isNew ? undefined : Number(customerId) })
      await qc.invalidateQueries({ queryKey: ['customers'] })
      notify(isNew ? `${customerName(form)} added` : 'Customer saved')
      nav(done)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Couldn’t save the customer.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="ci-form-page"><Spinner label="Loading customer…" /></div>

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to={done} label={done === '/master/customers' ? 'Customers' : 'Back'} />}
        title={isNew ? 'New customer' : customerName(form)}
        meta={isNew ? 'Someone you invoice or sell to on account.' : (form.email || form.phone || '')}
      />
      {error && <ErrorBanner message={error} />}
      <div className="ci-form-meta-card mf-form">
        <div className="ci-form-meta-top two">
          <Field label="First name" error={errors.first_name} hint="For a business, put its name here.">
            <input value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} />
          </Field>
          <Field label="Last name (optional)">
            <input value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} />
          </Field>
        </div>
        <div className="ci-form-meta-top two">
          <Field label="Email (optional)" error={errors.email}>
            <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="name@example.com" />
          </Field>
          <Field label="Phone (optional)">
            <input type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
        </div>
        <AddressFields form={form} set={set} />
      </div>
      {!isNew && has('jobs') && measurements && <CustomerMeasurements customerId={Number(customerId)} />}
      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}><Save size={14} />{saving ? 'Saving…' : (isNew ? 'Add customer' : 'Save')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(done)}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
